/**
 * Permissões do GRUPO de usuários somadas às do próprio usuário — a MESMA
 * regra das APIs (src/utils/permissoesGrupoUsuario.js), do backoffice e do app
 * Rotas. Mudou lá, muda aqui. Aqui só importa o que a impressão usa:
 *
 * - localizações: permitidas por nível se cruzam (vale o que os dois liberam),
 *   bloqueadas somam; "aplicar a restrição" vale se qualquer um ligar;
 * - categorias da impressão: interseção (vazia = sem restrição daquele lado;
 *   nada em comum = NENHUM, que não casa com id algum);
 * - administrador (perfil vazio ou com "admin") não é restrito.
 *
 * O GET do usuário traz o grupo à parte, em `grupo_usuario_dados`.
 */

export const NENHUM = "__nenhum__";
const NIVEIS = [1, 2, 3, 4];

type Entrada = Record<string, string | undefined>;
type NivelAcesso = Partial<Record<string, Entrada[]>>;
type Permissoes = { itens?: { visualizar?: boolean }; impressao_categorias?: string[] };
type ComPermissoes = {
  perfil?: string;
  niveis_acesso?: NivelAcesso[];
  perfil_personalizavel?: { permissoes?: Permissoes[] }[];
};
export type UsuarioComGrupo = ComPermissoes & { grupo_usuario_dados?: ComPermissoes | null };

export function ehAdmin(perfil: string | null | undefined): boolean {
  const p = String(perfil ?? "").trim().toLowerCase();
  return p === "" || p.includes("admin");
}

const textos = (v: unknown): string[] =>
  [...new Set((Array.isArray(v) ? v : []).map((x) => String(x ?? "").trim()).filter(Boolean))];

function cruzar(a: string[], b: string[]): string[] {
  if (!a.length) return b;
  if (!b.length) return a;
  const comum = a.filter((v) => b.includes(v));
  return comum.length ? comum : [NENHUM];
}

const permissoesDe = (u: ComPermissoes | null | undefined) =>
  (u?.perfil_personalizavel || []).flatMap((p) => p?.permissoes || []);

function idsDoNivel(acesso: NivelAcesso[] | undefined, chave: string, n: number): string[] {
  return textos(
    (acesso || []).flatMap((bloco) =>
      (bloco?.[chave] || []).map((e) => e?.[`id_nivel_loc${n}`]).filter((id) => id && id !== "*"),
    ),
  );
}

export function aplicarGrupoUsuario<T extends UsuarioComGrupo>(usuario: T | null | undefined): T | null | undefined {
  const grupo = usuario?.grupo_usuario_dados;
  if (!usuario || !grupo || ehAdmin(usuario.perfil)) return usuario;

  const u0 = permissoesDe(usuario)[0] || {};
  const g0 = permissoesDe(grupo)[0] || {};
  const visualizar = u0.itens?.visualizar === true || g0.itens?.visualizar === true;

  let niveis = usuario.niveis_acesso || [];
  if ((grupo.niveis_acesso || []).length && niveis.length) {
    const bloco: NivelAcesso = {};
    for (const n of NIVEIS) {
      bloco[`niveis${n}`] = cruzar(
        idsDoNivel(niveis, `niveis${n}`, n),
        idsDoNivel(grupo.niveis_acesso, `niveis${n}`, n),
      ).map((id) => ({ [`id_nivel_loc${n}`]: id }));
      bloco[`niveis${n}_bloqueados`] = textos([
        ...idsDoNivel(niveis, `niveis${n}_bloqueados`, n),
        ...idsDoNivel(grupo.niveis_acesso, `niveis${n}_bloqueados`, n),
      ]).map((id) => ({ [`id_nivel_loc${n}`]: id }));
    }
    niveis = [bloco];
  } else if ((grupo.niveis_acesso || []).length) {
    niveis = grupo.niveis_acesso || [];
  }

  const categorias = cruzar(
    textos(permissoesDe(usuario).flatMap((p) => p.impressao_categorias || [])),
    textos(permissoesDe(grupo).flatMap((p) => p.impressao_categorias || [])),
  );

  return {
    ...usuario,
    niveis_acesso: niveis,
    perfil_personalizavel: [
      { permissoes: [{ ...u0, itens: { ...(u0.itens || {}), visualizar }, impressao_categorias: categorias }] },
    ],
  };
}
